import { useState, useEffect, useCallback } from "react";
import { apiJson } from "../api/apiClient";

export function useWishlist(tenant, token) {
  const [wishlist,  setWishlist]  = useState([]);
  const [wishedIds, setWishedIds] = useState(new Set());
  const [loading,   setLoading]   = useState(false);

  const fetchWishlist = useCallback(async () => {
    if (!token || !tenant) return;
    setLoading(true);
    try {
      const data = await apiJson(tenant, "/wishlist", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (data.wishlists) {
        setWishlist(data.wishlists);
        setWishedIds(new Set(data.plot_ids.map(Number)));
      }
    } catch (e) {
      console.error("Wishlist fetch error", e);
    } finally {
      setLoading(false);
    }
  }, [tenant, token]);

  useEffect(() => {
    fetchWishlist();
  }, [fetchWishlist]);

  // ── Toggle: always hits /wishlist/toggle, backend decides add vs remove ──
  // forceAdd = true skips the stale wishedIds check (used after login when
  // wishedIds hasn't re-fetched yet with the new token)
  const toggle = useCallback(async (plotId, forceAdd = false) => {
    if (!token || !tenant) return;

    const id = Number(plotId);
    const isCurrentlyWished = wishedIds.has(id);
    const shouldAdd = forceAdd ? true : !isCurrentlyWished;

    // Optimistic update — flip immediately so UI feels instant
    setWishedIds(prev => {
      const next = new Set(prev);
      shouldAdd ? next.add(id) : next.delete(id);
      return next;
    });

    try {
      // apiJson already throws on any non-2xx response, so a 404/500
      // lands straight in the catch block below instead of being
      // silently parsed as if it were a valid toggle result.
      const data = await apiJson(tenant, "/wishlist/toggle", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plot_id: id }),
      });

      // Sync to authoritative server state
      setWishedIds(prev => {
        const next = new Set(prev);
        data.wishlisted ? next.add(id) : next.delete(id);
        return next;
      });

      // Refresh full wishlist so WishlistTab shows correct items
      fetchWishlist();

      return data;
    } catch (e) {
      // Revert optimistic update on network/server error
      setWishedIds(prev => {
        const next = new Set(prev);
        shouldAdd ? next.delete(id) : next.add(id);
        return next;
      });
      console.error("Wishlist toggle error", e);
      throw e; // let the caller (UnitPage) know it failed, so it can skip the success toast
    }
  }, [wishedIds, tenant, token, fetchWishlist]);

  // ── Remove by wishlist row ID (used from WishlistTab) ────────────────────
  const remove = useCallback(async (wishlistRowId, plotId) => {
    if (!token || !tenant) return;
    try {
      await apiJson(tenant, `/wishlist/${wishlistRowId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      setWishedIds(prev => { const n = new Set(prev); n.delete(Number(plotId)); return n; });
      setWishlist(prev => prev.filter(w => w.id !== wishlistRowId));
    } catch (e) {
      console.error("Wishlist remove error", e);
    }
  }, [tenant, token]);

  return { wishlist, wishedIds, loading, toggle, remove, refresh: fetchWishlist };
}